import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { fileUrl, formatApiErrorDetail } from "@/lib/api";
import { COUNTIES, CURRENCIES, AMENITIES, GENDER_PREF } from "@/data/constants";
import { useInstitutions } from "@/lib/useInstitutions";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ImagePlus, X, Loader2, Users } from "lucide-react";
import { toast } from "sonner";

export default function RoommateForm() {
  const navigate = useNavigate();
  const photoInput = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    title: "", post_type: "offering", institution: "", county: "", town: "", estate: "",
    current_roommates: "", spots_available: "", rent_share: "", currency: "KSH",
    gender_pref: "any", description: "", photos: [],
    contact_name: "", contact_phone: "", contact_email: "",
  });
  const [amen, setAmen] = useState([]);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const { institutions, addInstitution } = useInstitutions();
  const [newInst, setNewInst] = useState("");
  const [addingInst, setAddingInst] = useState(false);
  const toggleAmen = (a) => setAmen((p) => (p.includes(a) ? p.filter((x) => x !== a) : [...p, a]));

  const upload = async (files) => {
    const remaining = 10 - form.photos.length;
    setUploading(true);
    try {
      const out = [];
      for (const file of Array.from(files).slice(0, remaining)) {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/upload", fd); out.push(data.path);
      }
      setForm((p) => ({ ...p, photos: [...p.photos, ...out] }));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    setError("");
    if (!form.title || !form.county || !form.town || !form.rent_share || !form.contact_name || !form.contact_phone) {
      setError("Please fill in title, location, rent share and contact details."); return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form, amenities: amen, rent_share: Number(form.rent_share),
        current_roommates: form.current_roommates !== "" ? Number(form.current_roommates) : null,
        spots_available: form.spots_available !== "" ? Number(form.spots_available) : null,
      };
      const { data } = await api.post("/roommates", payload);
      toast.success("Roommate post published!");
      navigate(`/roommate/${data.id}`);
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="mx-auto max-w-3xl px-5 py-10 lg:px-8">
      <span className="label-caps text-[#D4A373]">Shared space</span>
      <h1 className="mt-2 flex items-center gap-2 font-display text-3xl font-medium tracking-tight text-[#1A1A1A]"><Users className="h-7 w-7 text-[#D4A373]" /> Post a roommate wanted</h1>

      <div className="mt-6 space-y-5 rounded-2xl border border-border/60 bg-white p-6">
        <div className="space-y-2">
          <Label className="label-caps text-[#6B7280]">I am *</Label>
          <div className="flex flex-col gap-3 sm:flex-row">
            {[{ v: "offering", l: "Offering a space (roommate wanted)" }, { v: "seeking", l: "Looking for a room / roommate" }].map((t) => (
              <button key={t.v} type="button" data-testid={`rm-type-${t.v}`} onClick={() => set("post_type", t.v)} className={`flex-1 rounded-xl border p-3 text-sm font-medium transition-colors ${form.post_type === t.v ? "border-[#2C3830] bg-[#2C3830] text-white" : "border-border text-[#2C3830] hover:border-[#2C3830]"}`}>{t.l}</button>
            ))}
          </div>
        </div>
        <div className="space-y-2">
          <Label className="label-caps text-[#6B7280]">Post title *</Label>
          <Input data-testid="rm-title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Roommate wanted in 2BR near Strathmore" className="rounded-xl" />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label className="label-caps text-[#6B7280]">Institution</Label>
            <Select value={form.institution || "none"} onValueChange={(v) => set("institution", v === "none" ? "" : v)}>
              <SelectTrigger data-testid="rm-institution" className="rounded-xl"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent><SelectItem value="none">Not specific</SelectItem>{institutions.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
            </Select>
            <div className="flex gap-2 pt-1">
              <Input
                data-testid="rm-new-institution"
                value={newInst}
                onChange={(e) => setNewInst(e.target.value)}
                placeholder="Can't find it? Add your institution"
                className="rounded-xl text-sm"
              />
              <Button
                type="button"
                data-testid="rm-add-institution"
                variant="outline"
                className="rounded-xl"
                disabled={newInst.trim().length < 2 || addingInst}
                onClick={async () => {
                  setAddingInst(true);
                  try {
                    const name = await addInstitution(newInst.trim());
                    set("institution", name);
                    setNewInst("");
                    toast.success("Institution added");
                  } catch (err) {
                    toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not add institution");
                  } finally {
                    setAddingInst(false);
                  }
                }}
              >
                Add
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="label-caps text-[#6B7280]">County *</Label>
            <Select value={form.county} onValueChange={(v) => set("county", v)}>
              <SelectTrigger data-testid="rm-county" className="rounded-xl"><SelectValue placeholder="Select county" /></SelectTrigger>
              <SelectContent>{COUNTIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="label-caps text-[#6B7280]">Town / area *</Label>
            <Input data-testid="rm-town" value={form.town} onChange={(e) => set("town", e.target.value)} className="rounded-xl" />
          </div>
          <div className="space-y-2">
            <Label className="label-caps text-[#6B7280]">Estate</Label>
            <Input data-testid="rm-estate" value={form.estate} onChange={(e) => set("estate", e.target.value)} className="rounded-xl" />
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-2">
            <Label className="label-caps text-[#6B7280]">Current roommates</Label>
            <Input data-testid="rm-current" type="number" min="0" max="8" value={form.current_roommates} onChange={(e) => set("current_roommates", e.target.value)} placeholder="e.g. 2" className="rounded-xl" />
          </div>
          <div className="space-y-2">
            <Label className="label-caps text-[#6B7280]">Spots open</Label>
            <Input data-testid="rm-spots" type="number" min="1" max="8" value={form.spots_available} onChange={(e) => set("spots_available", e.target.value)} placeholder="e.g. 1" className="rounded-xl" />
          </div>
          <div className="space-y-2">
            <Label className="label-caps text-[#6B7280]">Roommate gender preference</Label>
            <Select value={form.gender_pref} onValueChange={(v) => set("gender_pref", v)}>
              <SelectTrigger data-testid="rm-gender" className="rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>{GENDER_PREF.map((g) => <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="col-span-2 space-y-2">
            <Label className="label-caps text-[#6B7280]">{form.post_type === "seeking" ? "Monthly budget *" : "Rent share / month *"}</Label>
            <Input data-testid="rm-rent" type="number" value={form.rent_share} onChange={(e) => set("rent_share", e.target.value)} placeholder="e.g. 15000" className="rounded-xl" />
          </div>
          <div className="space-y-2">
            <Label className="label-caps text-[#6B7280]">Currency</Label>
            <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
              <SelectTrigger data-testid="rm-currency" className="rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-2">
          <Label className="label-caps text-[#6B7280]">Amenities</Label>
          <div className="flex flex-wrap gap-2">
            {AMENITIES.map((a) => (
              <button key={a} type="button" data-testid={`rm-amenity-${a}`} onClick={() => toggleAmen(a)} className={`rounded-full border px-3 py-1 text-xs transition-colors ${amen.includes(a) ? "border-[#2C3830] bg-[#2C3830] text-white" : "border-border text-[#6B7280] hover:border-[#2C3830]"}`}>{a}</button>
            ))}
          </div>
        </div>
        <div className="space-y-2">
          <Label className="label-caps text-[#6B7280]">Description</Label>
          <Textarea data-testid="rm-desc" value={form.description} onChange={(e) => set("description", e.target.value)} rows={4} placeholder="Describe the space, house rules, who you're looking for…" className="rounded-xl" />
        </div>
        <div className="space-y-2">
          <Label className="label-caps text-[#6B7280]">Photos</Label>
          <input ref={photoInput} type="file" accept="image/*" multiple hidden onChange={(e) => upload(e.target.files)} />
          <div className="grid grid-cols-4 gap-3">
            {form.photos.map((p, i) => (
              <div key={i} className="group relative aspect-square overflow-hidden rounded-xl border border-border">
                <img src={fileUrl(p)} alt="" className="h-full w-full object-cover" />
                <button onClick={() => set("photos", form.photos.filter((_, x) => x !== i))} className="absolute right-1 top-1 rounded-full bg-black/60 p-1 text-white opacity-0 group-hover:opacity-100"><X className="h-3 w-3" /></button>
              </div>
            ))}
            {form.photos.length < 10 && (
              <button data-testid="rm-add-photos" onClick={() => photoInput.current?.click()} disabled={uploading} className="flex aspect-square flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-border text-[#6B7280] hover:border-[#D4A373]">
                {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}<span className="text-xs">Add</span>
              </button>
            )}
          </div>
        </div>
        <div className="rounded-xl border border-border p-4">
          <p className="mb-3 font-medium text-[#2C3830]">Your contact (hidden until unlocked)</p>
          <div className="space-y-3">
            <Input data-testid="rm-contact-name" value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} placeholder="Your name *" className="rounded-xl" />
            <Input data-testid="rm-contact-phone" value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} placeholder="Phone *" className="rounded-xl" />
            <Input data-testid="rm-contact-email" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} placeholder="Email (optional)" className="rounded-xl" />
          </div>
        </div>
        {error && <p data-testid="rm-error" className="text-sm text-destructive">{error}</p>}
        <Button data-testid="rm-publish" onClick={submit} disabled={saving} className="w-full rounded-full bg-[#2C3830] py-6 text-white hover:bg-[#3A4A40]">
          {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Publishing…</> : "Publish roommate post"}
        </Button>
      </div>
    </div>
  );
}
